import { useState } from "react";
import { siteConfig } from "@/mocks/site";
import { aboutData, faqData } from "@/mocks/about";
import ScrollToTop from "@/components/base/ScrollToTop";
import PageMeta from "@/components/base/PageMeta";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";

const FORM_SUBMIT_URL = "https://readdy.ai/api/form/d9u43etca23r0tr1aq3g";

export default function AboutPage() {
  const [formState, setFormState] = useState<"idle" | "success" | "error">("idle");
  const [formMessage, setFormMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [faqOpen, setFaqOpen] = useState<number | null>(null);

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: `关于我们 - ${siteConfig.name}`,
    description: aboutData.brand.description,
    url: `${siteConfig.url}/about`,
    about: {
      "@type": "Organization",
      name: siteConfig.name,
      description: aboutData.brand.description,
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqData.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== "") {
      setFormState("success");
      setFormMessage("提交成功！感谢您的留言，我们会尽快回复您。");
      form.reset();
      return;
    }

    setSubmitting(true);
    setFormState("idle");

    try {
      const formData = new FormData(form);
      if (honeypotField) {
        formData.delete("phone_alt");
      }

      const response = await fetch(FORM_SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(
          Array.from(formData.entries()).map(([k, v]) => [k, String(v)])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not json
      }

      const serverCode = parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>).code : null;
      const serverMsg =
        (parsed && typeof parsed === "object" && (parsed as Record<string, unknown>).meta &&
          typeof (parsed as Record<string, unknown>).meta === "object" &&
          ((parsed as Record<string, unknown>).meta as Record<string, unknown>).message) ||
        (parsed && typeof parsed === "object" && (parsed as Record<string, unknown>).message) ||
        "";

      if (response.ok && serverCode === "OK") {
        setFormState("success");
        setFormMessage("提交成功！感谢您的留言，我们会尽快回复您。");
        form.reset();
      } else {
        const errorMsg = String(serverMsg || responseText || "提交失败，请稍后重试");
        if (errorMsg.includes("spam") || errorMsg.includes("form data is spam")) {
          setFormState("error");
          setFormMessage("提交失败，请稍后重试");
        } else {
          setFormState("error");
          setFormMessage(errorMsg);
        }
      }
    } catch {
      setFormState("error");
      setFormMessage("网络错误，请稍后重试");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageMeta
        title={`关于我们 - ${siteConfig.name}`}
        description={aboutData.brand.description}
        keywords="蜜桃影视,关于蜜桃影视,影视平台,联系我们,用户评价"
        canonicalUrl={`${siteConfig.url}/about`}
        schemaObjects={[aboutSchema, faqSchema]}
      />

      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="bg-background-100">
          <Breadcrumb
            items={[
              { label: "首页", path: "/" },
              { label: "关于我们" },
            ]}
          />
        </div>

        {/* Brand Section */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-14 md:py-20">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-4">
                {aboutData.brand.title}
              </h1>
              <p className="text-lg md:text-xl text-accent-500 font-heading mb-4">
                {aboutData.brand.subtitle}
              </p>
              <p className="text-base text-foreground-600 max-w-3xl mx-auto leading-relaxed">
                {aboutData.brand.description}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
              {aboutData.brand.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="text-center p-6 rounded-xl bg-background-50 border border-background-200/70"
                >
                  <div className="font-heading text-3xl md:text-4xl text-primary-500 mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                <h3 className="font-heading text-lg text-foreground-950 mb-2">我们的使命</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutData.brand.mission}</p>
              </div>
              <div className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                <h3 className="font-heading text-lg text-foreground-950 mb-2">我们的愿景</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{aboutData.brand.vision}</p>
              </div>
            </div>

            <h2 className="font-heading text-2xl text-foreground-950 mb-6 text-center">核心价值观</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {aboutData.brand.values.map((v) => (
                <div
                  key={v.title}
                  className="p-5 rounded-xl bg-background-100 border border-background-200/70"
                >
                  <h4 className="font-heading text-base text-foreground-950 mb-2">{v.title}</h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reviews Section */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-14 md:py-20 bg-background-100">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2 text-center">
              用户评价
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">听听我们的用户怎么说</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {aboutData.reviews.map((review) => (
                <div
                  key={review.id}
                  className="p-5 rounded-xl bg-background-50 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i
                        key={i}
                        className={`ri-star-fill text-sm ${
                          i < review.rating
                            ? "text-accent-500"
                            : "text-background-300"
                        }`}
                      ></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-3">
                    {review.content}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-500">
                    <span className="font-medium text-foreground-800">{review.name}</span>
                    <span>{review.date}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="w-full px-4 md:px-6 lg:px-8 py-14 md:py-20">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2">
                  {aboutData.contact.title}
                </h2>
                <p className="text-sm text-foreground-500 mb-6">{aboutData.contact.subtitle}</p>

                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">邮箱</p>
                      <p className="text-sm font-medium text-foreground-800">{aboutData.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">电话</p>
                      <p className="text-sm font-medium text-foreground-800">{aboutData.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">地址</p>
                      <p className="text-sm font-medium text-foreground-800">{aboutData.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-background-200 text-foreground-600">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">工作时间</p>
                      <p className="text-sm font-medium text-foreground-800">{aboutData.contact.workingHours}</p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 mt-6">
                  {aboutData.contact.socialMedia.map((sm) => (
                    <a
                      key={sm.name}
                      href={sm.url}
                      rel="nofollow"
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:text-primary-600 hover:bg-primary-100 transition-colors"
                      title={sm.name}
                    >
                      <i className={`${sm.icon} text-lg`}></i>
                    </a>
                  ))}
                </div>
              </div>

              <div className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                <h3 className="font-heading text-lg text-foreground-950 mb-4">给我们留言</h3>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form=""
                  className="space-y-4"
                >
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="contact-hp"
                  />

                  <div>
                    <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1">
                      姓名
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1">
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1">
                      留言内容
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（不超过500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-500 mt-1">不超过500个字符</p>
                  </div>

                  {formState === "success" && (
                    <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                      <p className="text-sm text-emerald-700">{formMessage}</p>
                    </div>
                  )}

                  {formState === "error" && (
                    <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                      <p className="text-sm text-red-700">{formMessage}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {submitting ? "提交中..." : "提交留言"}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="w-full px-4 md:px-6 lg:px-8 py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-2 text-center">
              常见问题
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">快速解答您的疑问</p>

            <div className="space-y-3">
              {faqData.map((faq, index) => (
                <div
                  key={index}
                  className="rounded-xl bg-background-50 border border-background-200/70 overflow-hidden"
                >
                  <button
                    className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer"
                    onClick={() => setFaqOpen(faqOpen === index ? null : index)}
                  >
                    <h4 className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</h4>
                    <i
                      className={`${faqOpen === index ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-foreground-500 text-lg transition-transform duration-200 flex-shrink-0`}
                    ></i>
                  </button>
                  {faqOpen === index && (
                    <div className="px-4 md:px-5 pb-4 md:pb-5">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}